import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Card,
  CardContent,
  Grid,
  Paper,
  TextField,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Download, PictureAsPdf, Refresh } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getInwardTransactions } from '../../services/api';
import CustomPagination from '../../components/common/CustomPagination';

const DEFAULT_PAGE_SIZE = 20;

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const statusColor = (status) => {
  const value = (status || '').toUpperCase();
  if (value === 'SUCCESS') return { bg: '#E8F5E9', color: '#2E7D32' };
  if (value === 'FAILED') return { bg: '#FDECEA', color: '#C62828' };
  if (value === 'RECEIVED') return { bg: '#E3F2FD', color: '#1565C0' };
  if (value === 'VERIFYING') return { bg: '#FFF8E1', color: '#B26A00' };
  return { bg: '#ECEFF1', color: '#37474F' };
};

const InwardTransactionsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sessionId, setSessionId] = useState('');
  const [appliedSessionId, setAppliedSessionId] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [customRowsInput, setCustomRowsInput] = useState(String(DEFAULT_PAGE_SIZE));

  const fetchPage = useCallback(async (targetPage) => {
    setLoading(true);
    setError('');
    try {
      const response = await getInwardTransactions({
        page: targetPage + 1,
        page_size: rowsPerPage,
        session_id: appliedSessionId || undefined,
      });
      setRows(Array.isArray(response?.results) ? response.results : []);
      setTotalCount(Number(response?.count || 0));
      setTotalPages(Number(response?.total_pages || 1));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.response?.data?.error || err?.message || 'Failed to load inward transactions');
      setRows([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedSessionId, rowsPerPage]);

  useEffect(() => {
    fetchPage(page);
  }, [page, fetchPage]);

  const rowsWithSerial = useMemo(
    () =>
      rows.map((row, idx) => ({
        ...row,
        sn: (page * rowsPerPage) + idx + 1,
      })),
    [rows, page, rowsPerPage]
  );

  const exportRows = useMemo(
    () =>
      rowsWithSerial.map((row) => ({
        sn: row.sn,
        session_id: row.session_id || '',
        transaction_reference: row.transaction_reference || '',
        payment_reference: row.payment_reference || '',
        transaction_id: row.transaction_id || '',
        originator_name: row.originator_name || '',
        originator_account: row.originator_account || '',
        bank_name: row.bank_name || '',
        bank_code: row.bank_code || '',
        cr_account: row.cr_account || '',
        amount: row.amount || '',
        transaction_type: row.transaction_type || '',
        status: row.status || '',
        settlement_status: row.settlement_status || '',
        send_status: row.send_status || '',
        created_at: formatDateTime(row.created_at),
      })),
    [rowsWithSerial]
  );

  const handleExportCsv = () => {
    const headers = [
      'S/N',
      'Session ID',
      'Transaction Ref',
      'Payment Ref',
      'Internal Txn ID',
      'Originator Name',
      'Originator Account',
      'Bank Name',
      'Bank Code',
      'Credit Account',
      'Amount',
      'Type',
      'Status',
      'Settlement',
      'Send Status',
      'Created At',
    ];

    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const body = exportRows.map((row) => [
      row.sn,
      row.session_id,
      row.transaction_reference,
      row.payment_reference,
      row.transaction_id,
      row.originator_name,
      row.originator_account,
      row.bank_name,
      row.bank_code,
      row.cr_account,
      row.amount,
      row.transaction_type,
      row.status,
      row.settlement_status,
      row.send_status,
      row.created_at,
    ]);

    const csv = [headers, ...body].map((line) => line.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `inward_transactions_page_${page + 1}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPdf = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(14);
    doc.text(`Inward Transactions - Page ${page + 1}`, 14, 14);

    autoTable(doc, {
      startY: 20,
      head: [[
        'S/N',
        'Session ID',
        'Txn Ref',
        'Payment Ref',
        'Originator',
        'CR Account',
        'Amount',
        'Status',
        'Settlement',
        'Created At',
      ]],
      body: exportRows.map((row) => [
        row.sn,
        row.session_id,
        row.transaction_reference,
        row.payment_reference,
        row.originator_name,
        row.cr_account,
        row.amount,
        row.status,
        row.settlement_status,
        row.created_at,
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [25, 118, 210] },
    });

    doc.save(`inward_transactions_page_${page + 1}.pdf`);
  };

  const columns = [
    { field: 'sn', headerName: 'S/N', width: 80, sortable: false, filterable: false },
    { field: 'session_id', headerName: 'Session ID', minWidth: 180, flex: 1 },
    { field: 'transaction_reference', headerName: 'Transaction Ref', minWidth: 190, flex: 1 },
    { field: 'payment_reference', headerName: 'Payment Ref', minWidth: 170, flex: 1 },
    { field: 'originator_name', headerName: 'Originator Name', minWidth: 170, flex: 1 },
    { field: 'originator_account', headerName: 'Originator Account', minWidth: 150, flex: 1 },
    { field: 'cr_account', headerName: 'Credit Account', minWidth: 150, flex: 1 },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const colors = statusColor(params.value);
        return (
          <Chip
            label={params.value || '-'}
            size="small"
            sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 700 }}
          />
        );
      },
    },
    {
      field: 'settlement_status',
      headerName: 'Settlement',
      width: 130,
      renderCell: (params) => <Typography variant="body2">{params.value || '-'}</Typography>,
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      minWidth: 170,
      flex: 1,
      renderCell: (params) => <Typography variant="body2">{formatDateTime(params.value)}</Typography>,
    },
  ];

  const handleSearch = () => {
    setPage(0);
    setAppliedSessionId(sessionId.trim());
  };

  const handleClearSearch = () => {
    setSessionId('');
    setAppliedSessionId('');
    setPage(0);
  };

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(String(rowsPerPage));
      return;
    }

    setRowsPerPage(Number(value));
    setCustomRowsActive(false);
    setPage(0);
  };

  const handleCustomRowsInputChange = (event) => {
    setCustomRowsInput(event.target.value.replace(/[^\d]/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const parsedValue = Number(customRowsInput);
    const nextValue = Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : DEFAULT_PAGE_SIZE;
    const boundedValue = Math.min(nextValue, 100);

    setRowsPerPage(boundedValue);
    setCustomRowsInput(String(boundedValue));
    setCustomRowsActive(false);
    setPage(0);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5">Inward Transactions</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => fetchPage(page)} disabled={loading}>
            Refresh
          </Button>
          <Button variant="outlined" startIcon={<Download />} onClick={handleExportCsv} disabled={loading || !rowsWithSerial.length}>
            Export CSV
          </Button>
          <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={handleExportPdf} disabled={loading || !rowsWithSerial.length}>
            Download PDF
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8, md: 4 }}>
              <TextField
                label="Session ID"
                variant="outlined"
                size="small"
                fullWidth
                value={sessionId}
                onChange={(event) => setSessionId(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
                placeholder="Search by session id"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 8 }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  flexWrap: 'wrap',
                }}
              >
                <Button variant="contained" onClick={handleSearch} disabled={loading}>
                  Search
                </Button>
                <Button variant="outlined" onClick={handleClearSearch} disabled={loading && !appliedSessionId}>
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ width: '100%', overflowX: 'auto', minHeight: 680 }}>
          <DataGrid
            rows={rowsWithSerial}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            disableRowSelectionOnClick
            hideFooter
            hideFooterPagination
            sx={{
              border: 0,
              minWidth: 1400,
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#DC143C', color: '#fff' },
              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#FFF0F3',
              },
            }}
          />
        </Box>
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Total: {totalCount} | Page {totalPages === 0 ? 0 : page + 1} of {totalPages} | {rowsPerPage} per page
          {appliedSessionId ? ` | Session ID: ${appliedSessionId}` : ''}
        </Typography>
      </Box>

      <Box sx={{ mt: 1 }}>
        <CustomPagination
          page={page}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
};

export default InwardTransactionsPage;
